import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Toast from "./Toast";
import "../../Product/Scheduling/scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";

export default function SpaceFormModal({ open, onClose, space, onSaved }) {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });

  const [form, setForm] = useState({
    name: "",
    location_building: "",
    location_floor: "",
    location_section: "",
    capacity: 1,
    description: "",
    tags: "",
    status: "active",
    inactive_reason: "",
    default_duration_min: 60,
    min_duration_min: 30,
    max_duration_min: 240,
    buffer_before_min: 0,
    buffer_after_min: 0,
    business_hours_enabled: false,
    business_hours_tz: "",
  });

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (space) {
      setForm({
        name: space.name || "",
        location_building: space.location_building || "",
        location_floor: space.location_floor || "",
        location_section: space.location_section || "",
        capacity: space.capacity || 1,
        description: space.description || "",
        tags: Array.isArray(space.tags) ? space.tags.join(", ") : "",
        status: space.status || "active",
        inactive_reason: space.inactive_reason || "",
        default_duration_min: space.default_duration_min || 60,
        min_duration_min: space.min_duration_min || 30,
        max_duration_min: space.max_duration_min || 240,
        buffer_before_min: space.buffer_before_min ?? 0,
        buffer_after_min: space.buffer_after_min ?? 0,
        business_hours_enabled: !!space.business_hours_enabled,
        business_hours_tz: space.business_hours_tz || "",
      });
    } else {
      setForm((p) => ({ ...p, name: "" }));
    }
  }, [open, space]);

  if (!open) return null;

  const validate = () => {
    if (!companyId) return "Company not loaded";
    if (!form.name || form.name.trim().length < 2)
      return "Space name is required (min 2 chars)";
    if (!Number(form.capacity) || Number(form.capacity) <= 0)
      return "Capacity must be greater than 0";
    if (Number(form.min_duration_min) <= 0) return "Min duration must be > 0";
    if (Number(form.max_duration_min) < Number(form.min_duration_min))
      return "Max duration must be >= min duration";
    if (
      Number(form.default_duration_min) < Number(form.min_duration_min) ||
      Number(form.default_duration_min) > Number(form.max_duration_min)
    )
      return "Default duration must be between min and max duration";
    if (
      form.status === "inactive" &&
      (!form.inactive_reason || form.inactive_reason.trim().length < 5)
    )
      return "Inactive reason is required (min 5 chars)";
    if (
      form.business_hours_enabled &&
      (!form.business_hours_tz || form.business_hours_tz.trim().length < 3)
    )
      return "Business hours timezone is required when enabled";
    if (Number(form.buffer_before_min) < 0 || Number(form.buffer_after_min) < 0)
      return "Buffer minutes must be >= 0";
    return "";
  };

  async function save() {
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setErr("");
    setBusy(true);

    try {
      const tags = form.tags
        ? form.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : null;

      const { data, error } = await supabase.rpc("rpc_upsert_space", {
        p_company_id: companyId,
        p_space_id: space?.id || null,
        p_name: form.name.trim(),
        p_location_building: form.location_building?.trim() || null,
        p_location_floor: form.location_floor?.trim() || null,
        p_location_section: form.location_section?.trim() || null,
        p_capacity: Number(form.capacity),
        p_description: form.description?.trim() || null,
        p_tags: tags,
        p_status: form.status,
        p_inactive_reason:
          form.status === "inactive"
            ? form.inactive_reason?.trim() || null
            : null,
        p_default_duration_min: Number(form.default_duration_min),
        p_min_duration_min: Number(form.min_duration_min),
        p_max_duration_min: Number(form.max_duration_min),
        p_buffer_before_min: Number(form.buffer_before_min),
        p_buffer_after_min: Number(form.buffer_after_min),
        p_business_hours_enabled: !!form.business_hours_enabled,
        p_business_hours_tz: form.business_hours_enabled
          ? form.business_hours_tz?.trim() || null
          : null,
      });

      if (error) throw error;

      setToast({ open: true, type: "success", message: "Space saved" });
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to save space");
      setToast({
        open: true,
        type: "error",
        message: e?.message || "Failed to save space",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sched-modal-backdrop" onMouseDown={onClose} />
      <div
        className="sched-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Space"
      >
        <div className="sched-modal-head">
          <div>
            <h3 className="sched-h3">{space ? "Edit Space" : "New Space"}</h3>
            <p className="sched-muted">
              Spaces are admin-controlled. Inactive spaces cannot be booked.
            </p>
          </div>
          <button
            className="sched-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="sched-form">
          <label className="sched-label">Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            disabled={busy}
          />

          <div className="sched-grid-3">
            <div>
              <label className="sched-label">Building</label>
              <input
                value={form.location_building}
                onChange={(e) =>
                  setForm((p) => ({ ...p, location_building: e.target.value }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">Floor</label>
              <input
                value={form.location_floor}
                onChange={(e) =>
                  setForm((p) => ({ ...p, location_floor: e.target.value }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">Section</label>
              <input
                value={form.location_section}
                onChange={(e) =>
                  setForm((p) => ({ ...p, location_section: e.target.value }))
                }
                disabled={busy}
              />
            </div>
          </div>

          <div className="sched-grid-2">
            <div>
              <label className="sched-label">Capacity</label>
              <input
                type="number"
                value={form.capacity}
                onChange={(e) =>
                  setForm((p) => ({ ...p, capacity: e.target.value }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">Tags (comma-separated)</label>
              <input
                value={form.tags}
                onChange={(e) =>
                  setForm((p) => ({ ...p, tags: e.target.value }))
                }
                disabled={busy}
              />
            </div>
          </div>

          <label className="sched-label">Description</label>
          <textarea
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
            disabled={busy}
            rows={3}
          />

          <div className="sched-divider" />

          <div className="sched-grid-3">
            <div>
              <label className="sched-label">Default (min)</label>
              <input
                type="number"
                value={form.default_duration_min}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    default_duration_min: e.target.value,
                  }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">Min (min)</label>
              <input
                type="number"
                value={form.min_duration_min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, min_duration_min: e.target.value }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">Max (min)</label>
              <input
                type="number"
                value={form.max_duration_min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, max_duration_min: e.target.value }))
                }
                disabled={busy}
              />
            </div>
          </div>

          <div className="sched-grid-2">
            <div>
              <label className="sched-label">Buffer before (min)</label>
              <input
                type="number"
                value={form.buffer_before_min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, buffer_before_min: e.target.value }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">Buffer after (min)</label>
              <input
                type="number"
                value={form.buffer_after_min}
                onChange={(e) =>
                  setForm((p) => ({ ...p, buffer_after_min: e.target.value }))
                }
                disabled={busy}
              />
            </div>
          </div>

          <div className="sched-divider" />

          <div className="sched-grid-2">
            <div>
              <label className="sched-label">Status</label>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm((p) => ({ ...p, status: e.target.value }))
                }
                disabled={busy}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="sched-label">Inactive reason</label>
              <input
                value={form.inactive_reason}
                onChange={(e) =>
                  setForm((p) => ({ ...p, inactive_reason: e.target.value }))
                }
                disabled={busy || form.status !== "inactive"}
                placeholder={
                  form.status === "inactive" ? "Why is it inactive?" : "—"
                }
              />
            </div>
          </div>

          <div className="sched-row">
            <label className="sched-check">
              <input
                type="checkbox"
                checked={form.business_hours_enabled}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    business_hours_enabled: e.target.checked,
                  }))
                }
                disabled={busy}
              />
              <span>Enable business hours enforcement</span>
            </label>
          </div>

          {form.business_hours_enabled && (
            <>
              <label className="sched-label">
                Business hours timezone (IANA)
              </label>
              <input
                value={form.business_hours_tz}
                onChange={(e) =>
                  setForm((p) => ({ ...p, business_hours_tz: e.target.value }))
                }
                disabled={busy}
                placeholder="e.g. Africa/Lagos"
              />
              <div className="sched-muted">
                Business hours rows can be configured in Supabase
                (space_business_hours table) or we can add a UI editor next.
              </div>
            </>
          )}

          {err ? <div className="sched-error">{err}</div> : null}

          <div className="sched-actions">
            <button
              className="sched-btn ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              className="sched-btn primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </>
  );
}
